import { useCallback } from 'react';
import useCreateMarketTradeTape from '@paradigm/features/src/unified-rfqs/ui/hooks/useCreateMarketTradeTape';
import {
  TradeTapeItem,
  getOppositeSide,
} from '@paradigm/features/src/unified-rfqs/entities/shared';
import { useUMPlatformCreationDisabled } from '@paradigm/features/src/platform-state/contexts/providers';
import { Maybe } from '@paradigm/utils/types';
import { isProductCodeDisabledForType } from '@paradigm/features/src/products/domain';
import { useQuoteBuilderModalMethods } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/modal-context';
import { exceptionLogger } from '@paradigm/logging/src/logging';
import { formatProductQty } from '@paradigm/features/src/unified-rfqs/ui/decimal-format';
import { useTrackAction } from '@paradigm/features/src/analytics';
import { TOAST_TIMEOUT_LONG, toast } from '../toast';
import PillButton from './PillButton';

const onError = (message: string) =>
  toast(message, { autoClose: TOAST_TIMEOUT_LONG });

export default function MatchQuoteButton({
  trade,
  productCode,
}: {
  productCode: Maybe<string>;
  trade: TradeTapeItem;
}) {
  const platformCreationDisabled = useUMPlatformCreationDisabled();
  const { refreshOb } = useCreateMarketTradeTape();
  const { open: openQuoteBuilder } = useQuoteBuilderModalMethods();
  const track = useTrackAction();
  const side = trade.rfqType === 'grfq' ? trade.action : trade.side;
  const label = side === 'BUY' ? 'Match Ask' : 'Match Bid';
  const ariaLabel = side === 'BUY' ? 'match ask' : 'match bid';
  const isDisabled =
    platformCreationDisabled || isProductCodeDisabledForType(productCode, 'OB');

  const handleClick = useCallback(() => {
    track('match quote', { side });
    refreshOb(trade, onError)
      .then((ob) => {
        if (ob == null) {
          toast('Could not refresh OB.');
          return;
        }
        openQuoteBuilder({
          rfqId: ob.id,
          side: getOppositeSide(side),
          quantity: formatProductQty(
            trade.quantity,
            ob.product_codes[0],
            trade.rfqType === 'drfq',
          ),
          price: trade.price,
          clickedQuote: null,
        });
      })
      .catch((error: Error) => {
        const logError = exceptionLogger('match quote from trade tape');
        logError(error);
        onError(error.message);
      });
  }, [openQuoteBuilder, refreshOb, side, track, trade]);

  return (
    <PillButton
      ariaLabel={ariaLabel}
      disabled={isDisabled}
      onClick={handleClick}
    >
      {label}
    </PillButton>
  );
}
